import { Injectable, OnDestroy } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  filter,
  fromEvent,
  merge,
  switchMap,
  timer,
  takeUntil,
} from 'rxjs';
import { Amplify, Auth, Storage } from 'aws-amplify';
import { environment } from 'src/environments/environment';
import { UserRoles } from 'src/app/shared/enums/roles-enum';
import { FirebaseApp } from '@angular/fire/app';
import { getMessaging, getToken } from '@angular/fire/messaging';
import { RegisteredDevicesService } from 'src/app/shared/services/registered-devices.service';
import { MatDialog } from '@angular/material/dialog';
import { LogoutCountdownComponent } from '../components/logout-countdown/logout-countdown.component';
import { Router } from '@angular/router';
import Swal from 'sweetalert2';
import {
  clearSessionAnchors,
  isSessionExpiredByAge,
} from 'src/app/shared/utils/session-age';
import {
  loginHrefWithReturn,
  loginReturnParams,
  readLoginId,
  writeNewLoginId,
} from 'src/app/shared/utils/session-tabs';
import { AlertService } from 'src/app/shared/services/alert.service';

export interface IUser {
  email: string;
  password: string;
  showPassword: boolean;
  code: string;
  name: string;
}

@Injectable({
  providedIn: 'root',
})
export class CognitoService implements OnDestroy {
  private authenticationSubject: BehaviorSubject<any>;
  minutesToMilliseconds = (minutes: number) => minutes * 60000;
  AUTO_LOGOUT_INTERVAL_WAIT = this.minutesToMilliseconds(0.05);

  private static CACHE_TTL = 30_000; // 30 seconds
  private _cachedSession: { value: any; ts: number } | null = null;
  private _cachedUser: { value: any; ts: number } | null = null;
  private _cachedAuthUser: { value: any; ts: number } | null = null;

  private isCacheValid(entry: { ts: number } | null): boolean {
    return !!entry && Date.now() - entry.ts < CognitoService.CACHE_TTL;
  }

  public clearCache(): void {
    this._cachedSession = null;
    this._cachedUser = null;
    this._cachedAuthUser = null;
  }

  /**
   * Corte por tope de 12 h en curso. Cumple dos funciones: cortar la recursión
   * (`signOut()` llama a `getUser()`, que vuelve a `getSession()`) y avisarle
   * al ErrorInterceptor que no superponga su propio modal de sesión expirada.
   */
  private _forcingLogout = false;

  /**
   * Momento del último corte por edad. Las peticiones que estaban en vuelo
   * cuando se cortó resuelven su 401 decenas de milisegundos después, ya con
   * `_forcingLogout` en false, y el ErrorInterceptor pisaba el aviso de 12 h
   * con su propio "Sesión expirada". La ventana de gracia las absorbe sin
   * dejar el flag colgado para siempre.
   */
  private _ageLogoutAt = 0;
  private static AGE_LOGOUT_GRACE_MS = 15_000;

  public isForcingLogout(): boolean {
    return (
      this._forcingLogout ||
      Date.now() - this._ageLogoutAt < CognitoService.AGE_LOGOUT_GRACE_MS
    );
  }

  /**
   * Login que esta pestaña vio por última vez. Si en `localStorage` hay otro,
   * alguien volvió a entrar desde otra pestaña y la sesión que esta pestaña
   * tiene en memoria ya no es la vigente. Ver `LOGIN_ID_KEY`.
   */
  private knownLoginId: string | null = readLoginId();

  /**
   * `true` si otra pestaña abrió una sesión después de la que conoce esta.
   *
   * Los cortes automáticos (tope de 12 h, 401 con sesión caída) lo consultan
   * antes de cerrar: cerrar acá borraría la sesión nueva de todas las
   * pestañas, porque `signOut()` limpia el `localStorage` compartido.
   */
  public hayLoginMasNuevo(): boolean {
    const actual = readLoginId();
    return actual !== null && actual !== this.knownLoginId;
  }

  /** Anota el login recién hecho y avisa a las demás pestañas. */
  private marcarLogin(): void {
    this.knownLoginId = writeNewLoginId();
  }

  /**
   * Página en la que está el usuario, para anotarla en el login.
   *
   * Si el corte ocurre en el arranque el router todavía no navegó y su `url`
   * es `/`; ahí la verdad está en la barra de direcciones.
   */
  private urlActual(): string {
    return this.router.navigated
      ? this.router.url
      : window.location.pathname + window.location.search;
  }

  /** Emite `true` al autenticarse y `false` al cerrar sesión. */
  public get authState$(): Observable<any> {
    return this.authenticationSubject.asObservable();
  }

  /** Resuelve igual si la promesa se cuelga, para no dejar el corte a medias. */
  private withDeadline<T>(promise: Promise<T>, ms = 5000): Promise<T | null> {
    return Promise.race([
      promise,
      new Promise<null>((resolve) => setTimeout(() => resolve(null), ms)),
    ]);
  }

  /**
   * Cierra la sesión que superó el tope de 12 h y devuelve al login.
   *
   * Navega antes de avisar para que el usuario no quede viendo una pantalla
   * privada detrás del modal. El aviso no se espera: si esto corre durante el
   * arranque (APP_INITIALIZER) no hay a quién bloquear todavía.
   */
  private async forceLogoutByAge(): Promise<void> {
    if (this._forcingLogout) return;

    // Lo que venció es la sesión vieja que esta pestaña tenía en memoria; en
    // otra ya se volvió a entrar. Se suelta el caché y la próxima consulta
    // toma la sesión nueva, sin cerrar nada.
    if (this.hayLoginMasNuevo()) {
      this.clearCache();
      return;
    }

    this._forcingLogout = true;
    this._ageLogoutAt = Date.now();
    // Se anota antes de navegar: al volver a entrar, esta pestaña regresa acá.
    const volver = this.urlActual();

    try {
      // `signOut()` no va a poder recuperar el email para dar de baja el token
      // de notificaciones, porque `getSession()` ya devuelve null. Es el precio
      // de cortar la recursión, y el dispositivo se re-registra al volver.
      //
      // Las dos esperas van contra reloj: si `Auth.signOut()` o una navegación
      // se cuelgan, el flag quedaría en true y la app entera se volvería un
      // agujero negro — todo 401, sin modal y sin redirección.
      await this.withDeadline(this.signOut());
    } catch {
      /* signOut() ya limpia el storage en su propio catch */
    }

    try {
      await this.withDeadline(
        this.router.navigate(['/auth/login'], {
          queryParams: loginReturnParams(volver),
        }),
      );
    } catch {
      // El router puede no estar listo si el corte ocurre en el arranque.
      window.location.assign(loginHrefWithReturn(volver));
    }

    // Se refresca al final para que la gracia cubra las peticiones que quedaron
    // en vuelo durante todo el corte, no sólo las del primer instante.
    this._ageLogoutAt = Date.now();
    this._forcingLogout = false;

    // `critico`, igual que el aviso del ErrorInterceptor: la sesión ya no
    // existe, así que este aviso pasa por encima de lo que haya en pantalla.
    this.alerts.fire(
      {
        icon: 'info',
        title: 'Sesión finalizada',
        text: 'Tu sesión alcanzó el límite de 12 horas. Inicia sesión nuevamente.',
        confirmButtonColor: '#1e5a3f',
      },
      'critico',
    );
  }

  private pauseInactiveListener = new BehaviorSubject<boolean>(false);
  private resetTimerSubscription = new Subscription();

  constructor(
    private afApp: FirebaseApp,
    private registeredDevices: RegisteredDevicesService,
    private dialog: MatDialog,
    private router: Router,
    private alerts: AlertService,
  ) {
    //this.initializeInactiveListener();
    Amplify.configure({
      Auth: environment.cognito,
      Storage: {
        AWSS3: environment.s3,
      },
    });
    this.authenticationSubject = new BehaviorSubject<boolean>(false);
  }

  openLogoutModal() {
    // Pausar la escucha de eventos cuando se abre el modal para validar
    // si el usuario desea continuar con la sesión abierta
    this.pauseInactiveListener.next(true);
    const dialogRef = this.dialog.open(LogoutCountdownComponent, {
      width: '350px',
      data: {},

      disableClose: true,
    });
    // Cuando se cierre la sesión, el modal devolvera un booleano
    // que sera true o false dependiendo de si se desea o no
    // continuar con la sesión abierta
    dialogRef.afterClosed().subscribe((keepSession) => {
      if (keepSession) {
        // Refrescar la sesión y luego habilita la escucha de eventos
        this.getSession().then(() => {
          this.pauseInactiveListener.next(false);
        });
      } else {
        // Ejecuta el logout
        this.signOut().then(() => {
          this.router.navigate([['auth/login']]);
        });
      }
    });
  }

  initializeInactiveListener() {
    //Escuchar los eventos del mouse que tomamos en cuenta
    // para determinar que el usuario esta interactuando con la página
    const mouseMove$ = fromEvent(document, 'mousemove');
    const click$ = fromEvent(document, 'click');
    // Pausar la escucha de los eventos cuando el observable de pausa esta
    // habilitado, esto se ejecuta al abrir el modal de log out.
    this.resetTimerSubscription = merge(mouseMove$, click$)
      .pipe(
        filter(() => !this.pauseInactiveListener.value),
        // Usando el switchmap se cancela el observable anterior si
        // todavía no se ejecuta, en este caso luego de 10min y
        // se completa el observable si una nueva emisión es ejecutada
        switchMap(() => {
          return timer(this.AUTO_LOGOUT_INTERVAL_WAIT).pipe(
            takeUntil(merge(mouseMove$, click$)),
          );
        }),
      )
      .subscribe(() => {
        // Al obtener una emisión que cumple las validaciones antes
        // añadidas, se verifica la validez de la sesión y si no es
        // válida, se valida el observable de pausa, si esta desactivado
        // abre la ventana de logout de modal
        this.getSession().then((credentials) => {
          if (credentials) {
            if (!this.pauseInactiveListener.value) {
              this.openLogoutModal();
            }
          }
        });
      });
  }

  private cleanComponentSubscriptions(): void {
    this.authenticationSubject.complete();
  }

  // Limpiar la suscripción del timer cuando el componente se destruye
  ngOnDestroy() {
    //this.cleanComponentSubscriptions();
  }

  public signUp(
    email: string,
    password: string,
    isCompany: number,
    role: string = UserRoles.aguacate_client,
  ): Promise<any> {
    return Auth.signUp({
      username: email,
      password: password,
      attributes: {
        'custom:user-group': role,
        'custom:is-company': `${isCompany}`,
      },
    });
  }
  public resendConfirmationCode(email: string): Promise<any> {
    return Auth.resendSignUp(email);
  }
  public resetPassword(email: string) {
    return Auth.forgotPassword(email);
  }

  checkTokenCloudMessage(email: string) {
    const messaging = getMessaging(this.afApp);
    getToken(messaging, { vapidKey: environment.firebase.applicationServerKey })
      .then((currentToken) => {
        if (currentToken) {
          this.registeredDevices.deleteDevice(email, currentToken);
        }
      })
      .catch(() => {});
  }

  public completeResetPassword(email: string, code: string, password: string) {
    return Auth.forgotPasswordSubmit(email, code, password);
  }
  public confirmSignUp(email: string, code: string): Promise<any> {
    return Auth.confirmSignUp(email, code);
  }

  public async signIn(email: string, password: string): Promise<void> {
    this.clearCache();
    // Arrancamos de cero: si la sesión anterior se fue sin pasar por signOut(),
    // su marcador de inicio y su contador de inactividad seguirían en
    // localStorage y cerrarían la sesión que se está por abrir.
    clearSessionAnchors();
    // Red de seguridad: si un corte anterior dejó el flag colgado, un login
    // nuevo lo destraba sin necesidad de recargar la página.
    this._forcingLogout = false;
    this._ageLogoutAt = 0;
    try {
      const response = await Auth.signIn(email, password);
      if (response.challengeName === 'SOFTWARE_TOKEN_MFA') {
        const codigo = await this.pedirCodigoMfa();
        if (codigo) {
          await this.confirmSignIn(response, codigo);
          this.marcarLogin();
          this.authenticationSubject.next(true);
        }
      } else {
        this.marcarLogin();
        this.authenticationSubject.next(true);
      }
    } catch (err) {
      throw err;
    }
  }

  public async signOut(): Promise<void> {
    this.clearCache();
    try {
      const user = await this.getUser();
      const email = user?.attributes?.email;

      if (email) this.checkTokenCloudMessage(email);

      this.clearCache();
      await Auth.signOut();

      this.authenticationSubject.next(false);

      localStorage.clear();
      sessionStorage.clear();
    } catch (err) {
      localStorage.clear();
      sessionStorage.clear();
      this.authenticationSubject.next(false);
    } finally {
      this.clearCache();
    }
  }

  private async getCurrentUser(): Promise<any> {
    try {
      return await Auth.currentAuthenticatedUser();
    } catch {
      return null;
    }
  }

  public startMFASetup(): Promise<any> {
    return Auth.currentAuthenticatedUser().then((user) => {
      return Auth.setupTOTP(user);
    });
  }

  /**
   * Inicializa TOTP en Cognito y devuelve tanto el otpauth URI (para QR)
   * como el secret crudo (para que el usuario lo tipee manualmente en su
   * Authenticator si no puede escanear el QR).
   *
   * Importante: se llama una sola vez por sesión de setup. Cada call a
   * `Auth.setupTOTP` re-genera el secret, así que mantener cacheado el
   * resultado en el componente hasta cerrar el modal.
   */
  public async generateQRCode(): Promise<{ uri: string; secret: string }> {
    const user = await Auth.currentAuthenticatedUser();
    const secret = await Auth.setupTOTP(user);
    const issuer = encodeURIComponent('Aguacate Wallet');
    const label = encodeURIComponent(user.attributes.email);
    const uri = `otpauth://totp/${issuer}:${label}?secret=${secret}&issuer=${issuer}`;
    return { uri, secret };
  }
  public verifySoftwareToken(code: string): Promise<any> {
    return Auth.currentAuthenticatedUser().then((user) => {
      return Auth.verifyTotpToken(user, code);
    });
  }

  /**
   * Modal del código de verificación en dos pasos (TOTP) al iniciar sesión.
   *
   * Es SOLO la capa de UI que recolecta los 6 dígitos: reemplaza el input de
   * texto pelado por las mismas casillas que usa el registro (`app-otp-input`),
   * explica que el código sale de Google Authenticator y lo valida Cognito en
   * `confirmSignIn`. Devuelve el código, o `null` si el usuario cancela.
   *
   * Un solo intento, igual que antes: si el código es incorrecto,
   * `confirmSignIn` lanza y `signIn` propaga el error (el login se reintenta
   * desde cero). No se toca la operación del código (generación/validación).
   */
  private async pedirCodigoMfa(): Promise<string | null> {
    const LEN = 6;
    const cajas = Array.from(
      { length: LEN },
      (_, i) =>
        `<input class="mfaotp-box" type="text" inputmode="numeric" pattern="[0-9]*" maxlength="1" autocomplete="one-time-code" aria-label="Dígito ${i + 1}" />`,
    ).join('');

    const html = `
      <div style="text-align:center">
        <div style="width:56px;height:56px;border-radius:50%;background:#e7f0ea;display:inline-flex;align-items:center;justify-content:center;margin:2px auto 14px">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#1e5a3f" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path><path d="M9 12l2 2 4-4"></path></svg>
        </div>
        <div style="font-size:1.05rem;font-weight:700;color:#142019;margin-bottom:6px">Verificación en dos pasos</div>
        <div style="font-size:.88rem;color:#5c6b62;line-height:1.5;max-width:300px;margin:0 auto">Abre <b>Google Authenticator</b> y escribe el código de 6 dígitos que muestra para tu cuenta de Aguacate.</div>
        <div style="display:inline-flex;gap:6px;align-items:center;margin-top:12px;font-size:.76rem;color:#1e5a3f;background:#e7f0ea;padding:5px 11px;border-radius:999px;font-weight:600">Google Authenticator</div>
        <div style="display:flex;justify-content:center;gap:8px;margin:20px 0 6px">${cajas}</div>
        <div style="font-size:.76rem;color:#8a8f98;margin-top:6px">El código cambia cada 30 segundos en Google Authenticator</div>
      </div>
      <style>
        .mfaotp-box{width:46px;height:54px;text-align:center;font-size:1.7rem;font-weight:600;border:2px solid #1e5a3f;border-radius:12px;outline:none;color:#1e5a3f;caret-color:#1e5a3f;transition:box-shadow .15s,border-color .15s;padding:0;box-sizing:border-box}
        .mfaotp-box:focus{border-color:#154e3a;box-shadow:0 0 0 3px rgba(30,90,63,.15)}
      </style>`;

    const codigoDe = () =>
      Array.from(document.querySelectorAll<HTMLInputElement>('.mfaotp-box'))
        .map((i) => i.value)
        .join('');

    const { isConfirmed, value } = await Swal.fire({
      html,
      showCancelButton: true,
      confirmButtonText: 'Verificar',
      confirmButtonColor: '#1e5a3f',
      cancelButtonText: 'Cancelar',
      focusConfirm: false,
      didOpen: () => {
        const inputs = Array.from(
          document.querySelectorAll<HTMLInputElement>('.mfaotp-box'),
        );
        inputs.forEach((input, idx) => {
          input.addEventListener('input', () => {
            input.value = input.value.replace(/\D/g, '').slice(-1);
            if (input.value && idx < LEN - 1) inputs[idx + 1].focus();
            if (codigoDe().length === LEN) Swal.clickConfirm();
          });
          input.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Backspace' && !input.value && idx > 0) {
              inputs[idx - 1].focus();
              inputs[idx - 1].value = '';
            }
          });
          input.addEventListener('paste', (e: ClipboardEvent) => {
            e.preventDefault();
            const texto = (e.clipboardData?.getData('text') || '')
              .replace(/\D/g, '')
              .slice(0, LEN);
            texto.split('').forEach((d, k) => (inputs[k].value = d));
            (inputs[Math.min(texto.length, LEN - 1)] ?? inputs[LEN - 1]).focus();
            if (codigoDe().length === LEN) Swal.clickConfirm();
          });
        });
        inputs[0]?.focus();
      },
      preConfirm: () => {
        const codigo = codigoDe();
        if (codigo.length !== LEN) {
          Swal.showValidationMessage('Ingresa los 6 dígitos del código');
          return false;
        }
        return codigo;
      },
    });

    return isConfirmed ? (value as string) : null;
  }

  public confirmSignIn(user: any, code: string): Promise<any> {
    return Auth.confirmSignIn(user, code, 'SOFTWARE_TOKEN_MFA');
  }

  public async enableMFA(): Promise<void> {
    const user = await this.getCurrentUser();
    if (user) await Auth.setPreferredMFA(user, 'TOTP');
  }

  public async disableMFA(): Promise<void> {
    const user = await this.getCurrentUser();
    if (user) await Auth.setPreferredMFA(user, 'NOMFA');
  }

  public async getMFAStatus(): Promise<string | undefined> {
    const user = await this.getCurrentUser();
    return user ? Auth.getPreferredMFA(user) : undefined;
  }

  public isAuthenticated(): Promise<boolean> {
    if (this.authenticationSubject.value) {
      return Promise.resolve(true);
    } else {
      return this.getUser()
        .then(
          (user: any) => {
            if (user) {
              return true;
            } else {
              return false;
            }
          },
          (error) => {
            return false;
          },
        )
        .catch((err) => {
          return false;
        });
    }
  }

  public async getUser(): Promise<any> {
    if (this.isCacheValid(this._cachedUser)) {
      return this._cachedUser!.value;
    }
    try {
      const session = await this.getSession();
      if (session) {
        const user = await Auth.currentUserInfo();
        this._cachedUser = { value: user, ts: Date.now() };
        return user;
      }
    } catch (err) {
      // No hay sesión activa
    }
    return null;
  }

  public async getAuthUser(): Promise<any> {
    if (this.isCacheValid(this._cachedAuthUser)) {
      return this._cachedAuthUser!.value;
    }
    try {
      const session = await this.getSession();
      if (session) {
        const user = await Auth.currentAuthenticatedUser();
        this._cachedAuthUser = { value: user, ts: Date.now() };
        return user;
      }
    } catch (err) {
      // No hay usuario autenticado
    }
    return null;
  }

  // Evita llamadas duplicadas simultáneas a Auth.currentSession()
  private _pendingSessionPromise: Promise<any> | null = null;

  public async getSession(): Promise<any> {
    // Durante el corte por edad no hay sesión que devolver, y responder acá
    // evita que `signOut()` se llame a sí mismo a través de `getUser()`.
    if (this._forcingLogout) {
      return null;
    }

    // Otra pestaña volvió a entrar: la sesión en caché es la vieja. Se descarta
    // antes de medirle el tope, que si no la daría por vencida y cortaría.
    if (this._cachedSession && this.hayLoginMasNuevo()) {
      this._cachedSession = null;
    }

    if (this.isCacheValid(this._cachedSession)) {
      const cached = this._cachedSession!.value;
      // El caché dura 30 s, así que el tope hay que medirlo también acá: si no,
      // una sesión podía seguir viva media vuelta más de lo debido.
      if (isSessionExpiredByAge(cached)) {
        this._cachedSession = null;
        void this.forceLogoutByAge();
        return null;
      }
      return cached;
    }
    // Si ya hay una petición en curso, reusar la misma promise
    if (this._pendingSessionPromise) {
      return this._pendingSessionPromise;
    }
    this._pendingSessionPromise = this._fetchSession();
    try {
      return await this._pendingSessionPromise;
    } finally {
      this._pendingSessionPromise = null;
    }
  }

  private async _fetchSession(): Promise<any> {
    try {
      // Timeout de 10s: Auth.currentSession() puede colgar indefinidamente
      // cuando hay tokens expirados y Cognito intenta refrescarlos. 4s era
      // poco: un refresh legítimo contra Cognito puede tardar más y el race
      // devolvía null con la sesión a punto de llegar.
      const sessionPromise = Auth.currentSession();
      const timeout = new Promise<null>(resolve =>
        setTimeout(() => resolve(null), 10000)
      );
      const session = await Promise.race([sessionPromise, timeout]);

      // Tope de 12 h desde el login. Va antes de cachear para que ninguna capa
      // de arriba (interceptor, guards, socket) llegue a ver la sesión vencida.
      if (session && isSessionExpiredByAge(session)) {
        this._cachedSession = null;
        void this.forceLogoutByAge();
        return null;
      }

      // Cachear SOLO sesiones reales. Cachear null por 30s envenenaba el
      // cache: TODO request de esa ventana recibía el 401 sintético del
      // ApiRestInterceptor ("Tu sesión expiró") aunque el refresh de Amplify
      // terminara bien un segundo después. Las ráfagas concurrentes ya las
      // dedup-ea `_pendingSessionPromise`; no hace falta cachear el fallo.
      this._cachedSession = session ? { value: session, ts: Date.now() } : null;
      // La sesión que se acaba de leer es la vigente en el navegador: si vino
      // de un login hecho en otra pestaña, esta pestaña la adopta.
      if (session) this.knownLoginId = readLoginId();
      return session;
    } catch {
      // Sin usuario logueado currentSession() rechaza RÁPIDO (no hay red de
      // por medio); no cachear el fallo tampoco acá.
      this._cachedSession = null;
      return null;
    }
  }

  public updateUser(user: IUser): Promise<any> {
    return Auth.currentUserPoolUser().then((cognitoUser: any) => {
      return Auth.updateUserAttributes(cognitoUser, user);
    });
  }
}
